import { IProjectRole } from "@kuika/kuika-cl-model";
import React from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaMenuItem from "../../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaOverflowText from "../../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTextMedium from "../../../../ui-kit/typography/TextMedium/TextMedium";
import { EditingState } from "./role-management-modal";
import { RoleRow } from "./role-row";

interface FolderItemProps {
  folder: any;
  level?: number;
  search: string;
  roles: IProjectRole[];
  expandedIds: string[];
  editing: EditingState | null;
  screens: any[];
  onToggleExpand: (id: string) => void;
  onAddRole: (folderId: string) => void;
  onAddSubFolder: (parentFolderId: string) => void;
  onEditFolder: (folder: any) => void;
  onDeleteFolder: (folder: any) => void;
  onStartEditRole: (role: IProjectRole) => void;
  onConfirmEditRole: (role: IProjectRole) => void;
  onCancelEdit: () => void;
  onUpdateField: (field: keyof EditingState, value: string) => void;
  onDeleteRole: (role: IProjectRole) => void;
}

export const FolderItem: React.FC<FolderItemProps> = ({
  folder,
  level = 0,
  search,
  roles,
  expandedIds,
  editing,
  screens,
  onToggleExpand,
  onAddRole,
  onAddSubFolder,
  onEditFolder,
  onDeleteFolder,
  onStartEditRole,
  onConfirmEditRole,
  onCancelEdit,
  onUpdateField,
  onDeleteRole
}) => {
  const isExpanded = expandedIds.includes(folder.id);
  const folderRoles = (folder.roleIds || [])
    .map((rid: string) => roles.find((r) => r.id === rid))
    .filter(Boolean)
    .filter((role: any) => role.name.toLowerCase().includes(search.toLowerCase()) || !search);

  const addMenuOptions = [
    {
      key: "add-role",
      label: "Add Role",
      onClick: () => onAddRole(folder.id)
    },
    ...(level === 0
      ? [
          {
            key: "add-subfolder",
            label: "Add Sub-folder",
            onClick: () => onAddSubFolder(folder.id)
          }
        ]
      : [])
  ];

  const folderMenuOptions = [
    {
      key: "edit",
      label: "Edit",
      onClick: () => onEditFolder(folder)
    },
    {
      key: "delete",
      label: "Delete",
      onClick: () => onDeleteFolder(folder)
    }
  ];

  return (
    <Droppable droppableId={folder.id} key={folder.id}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.droppableProps}>
          <KuikaMenuItem
            onClick={() => onToggleExpand(folder.id)}
            title={
              <div className="role-management-folder-content" style={{ paddingLeft: `${level * 24}px` }}>
                <div className="kuika-flex-center--gap-xs">
                  <KuikaButton
                    isIcon
                    type="tertiary"
                    color="neutral"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleExpand(folder.id);
                    }}
                    aria-label="Toggle"
                  >
                    <KuikaSvg name={isExpanded ? "k-down" : "k-right"} type="arrows" width={16} height={16} />
                  </KuikaButton>
                  <div
                    className="kuika-flex-center--gap-s"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleExpand(folder.id);
                    }}
                    style={{ cursor: "pointer", flex: 1, minWidth: 0 }}
                  >
                    <KuikaSvg name="folder" type="sidebar-icons" width={16} height={16} />
                    {/* The menu item only auto-wraps a plain-string title; this one is a node, so the
                        name opts into the row's hover reveal here. */}
                    <KuikaTextMedium style={{ display: "block", minWidth: 0 }}>
                      <KuikaOverflowText>{folder.name}</KuikaOverflowText>
                    </KuikaTextMedium>
                  </div>
                </div>
              </div>
            }
            rightDropdown={
              <>
                <KuikaDropdownMenu options={addMenuOptions}>
                  <KuikaButton isIcon type="tertiary" color="neutral" size="sm" aria-label="Add menu">
                    <KuikaSvg name="plus" width={16} height={16} />
                  </KuikaButton>
                </KuikaDropdownMenu>
                <KuikaDropdownMenu options={folderMenuOptions}>
                  <KuikaButton isIcon type="tertiary" color="neutral" size="sm" aria-label="Folder menu">
                    <KuikaSvg name="dots" type="common" width={16} height={16} />
                  </KuikaButton>
                </KuikaDropdownMenu>
              </>
            }
          />
          {isExpanded && (
            <>
              {folderRoles.map((role: any, index: number) => (
                <Draggable key={role.id} draggableId={role.id} index={index}>
                  {(p, snapshot) => (
                    <div
                      ref={p.innerRef}
                      {...p.draggableProps}
                      {...p.dragHandleProps}
                      className={`${snapshot.isDragging ? "dragging" : ""}`}
                      style={
                        {
                          opacity: snapshot.isDragging ? 0.5 : 1,
                          ...p.draggableProps.style
                        } as any
                      }
                    >
                      <RoleRow
                        role={role}
                        level={level + 1}
                        editing={editing}
                        screens={screens}
                        showDragHandle={true}
                        onStartEdit={onStartEditRole}
                        onConfirmEdit={onConfirmEditRole}
                        onCancelEdit={onCancelEdit}
                        onUpdateField={onUpdateField}
                        onDeleteRole={onDeleteRole}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </>
          )}
        </div>
      )}
    </Droppable>
  );
};
